import React from "react";
import { Head, Link, usePage } from "@inertiajs/react";
import styled from "styled-components";
import { GlobalStyle } from "../../constants/global-style";
import ProvideLoggedInUser from "../../providers/ProvideLoggedInUser";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import ExamApp from "../../exams/App";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";

// Inertia entry for `/review_exam/:id` when `exam_rebrand_v1` is ON.
// Read-only score view + per-question key (correct = green, user's
// wrong choice = red).
export default function Review() {
  const {
    theme = "light",
    package: pkg = {},
    result = {},
    questions = [],
    endpoints = {},
  } = usePage().props;

  return (
    <>
      <Head>
        <title>{`Hasil Ujian — ${pkg.curriculum_name || ""}`}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <GlobalStyle />
      <ProvideLoggedInUser>
        <ExamApp theme={theme}>
          <Inner>
            <SectionErrorBoundary name="ResultsHero">
              <Hero $passed={result.passed}>
                <HeroEyebrow>{result.passed ? "Ujian Lulus" : "Belum Lulus"}</HeroEyebrow>
                <HeroTitle>{pkg.curriculum_name}</HeroTitle>
                <HeroMeta>
                  Skor: <strong>{result.score ?? "—"}</strong>
                  {pkg.minimum_score != null && (
                    <> · Min. Lulus: <strong>{pkg.minimum_score}</strong></>
                  )}
                </HeroMeta>
                <HeroActions>
                  {endpoints.back_url && (
                    <SecondaryAction href={endpoints.back_url}>← Kembali ke Modul</SecondaryAction>
                  )}
                  {!result.passed && endpoints.remedial_url && (
                    <PrimaryAction href={endpoints.remedial_url}>Ujian Remedial →</PrimaryAction>
                  )}
                </HeroActions>
              </Hero>
            </SectionErrorBoundary>

            <SectionErrorBoundary name="QuestionsReview">
              <QuestionsList>
                {questions.map((q) => (
                  <QuestionCard key={q.id} $correct={q.user_correct}>
                    <QHead>
                      <QNumber>No. {q.position + 1}</QNumber>
                      <QStatus $correct={q.user_correct}>
                        {q.user_correct ? "Benar" : "Salah"}
                      </QStatus>
                    </QHead>
                    <QBody dangerouslySetInnerHTML={{ __html: q.body_html }} />
                    <Options>
                      {q.options.map((opt, i) => {
                        const letter = String.fromCharCode(65 + i);
                        let status = "neutral";
                        if (opt.is_correct) status = "correct";
                        if (opt.is_user_choice && !opt.is_correct) status = "wrong";
                        return (
                          <OptionRow key={opt.id} $status={status}>
                            <OptionLetter $status={status}>{letter}</OptionLetter>
                            <OptionBody dangerouslySetInnerHTML={{ __html: opt.body_html }} />
                            {opt.is_correct && <Tag $status="correct">Jawaban benar</Tag>}
                            {opt.is_user_choice && !opt.is_correct && (
                              <Tag $status="wrong">Pilihan Anda</Tag>
                            )}
                          </OptionRow>
                        );
                      })}
                    </Options>
                  </QuestionCard>
                ))}
              </QuestionsList>
            </SectionErrorBoundary>
          </Inner>
        </ExamApp>
      </ProvideLoggedInUser>
    </>
  );
}

// ---- Styles ----------------------------------------------------------------

const Inner = styled.div`
  ${outerContainerCss}
`;

const Hero = styled.div`
  background: ${({ $passed }) =>
    $passed
      ? "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(16,185,129,0.04))"
      : "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"};
  border: 1px solid
    ${({ $passed }) =>
      $passed ? "rgba(16, 185, 129, 0.32)" : "rgba(216, 89, 7, 0.32)"};
  border-radius: 18px;
  padding: 28px 32px;
  margin-bottom: 22px;
  box-shadow: ${card_shadow_md};

  @media ${narrowScreenMediaQuery} { padding: 22px 18px; }

  [data-theme="dark"] & {
    background: ${({ $passed }) =>
      $passed
        ? "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.08))"
        : "linear-gradient(135deg, rgba(216, 89, 7, 0.18), rgba(247, 179, 12, 0.08))"};
  }
`;

const HeroEyebrow = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.6);
  color: #1f2937;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  [data-theme="dark"] & { background: rgba(0, 0, 0, 0.3); color: #f3f4f6; }
`;

const HeroTitle = styled.h1`
  margin: 8px 0 4px;
  font-size: 30px;
  font-weight: 800;
  color: #1f2937;

  [data-theme="dark"] & { color: #f3f4f6; }
`;

const HeroMeta = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  color: #4b5563;

  strong { color: ${brand_orange_deep}; font-variant-numeric: tabular-nums; }

  [data-theme="dark"] & { color: #cbd5e1; }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;

  &:hover { filter: brightness(0.97); color: #fff; }
`;

const SecondaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  border-radius: 999px;
  background: transparent;
  color: ${brand_orange_deep};
  border: 1px solid ${brand_orange_deep};
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;

  &:hover { background: rgba(216, 89, 7, 0.08); color: ${brand_orange_deep}; }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 22px 26px;
  box-shadow: ${card_shadow_md};

  @media ${narrowScreenMediaQuery} { padding: 18px 18px; }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
`;

const QHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const QNumber = styled.span`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 8px;
  background: #1f2937;
  color: #ffffff;
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.06em;
`;

const QStatus = styled.span`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $correct }) =>
    $correct ? "rgba(16, 185, 129, 0.16)" : "rgba(220, 38, 38, 0.14)"};
  color: ${({ $correct }) => ($correct ? "#047857" : "#b91c1c")};
`;

const QBody = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: #1f2937;
  margin-bottom: 14px;

  [data-theme="dark"] & { color: #e5e7eb; }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ $status }) =>
    $status === "correct" ? "rgba(16, 185, 129, 0.10)" :
    $status === "wrong"   ? "rgba(220, 38, 38, 0.10)"  :
                            "rgba(15, 23, 42, 0.02)"};
  border: 1px solid ${({ $status }) =>
    $status === "correct" ? "rgba(16, 185, 129, 0.32)" :
    $status === "wrong"   ? "rgba(220, 38, 38, 0.32)"  :
                            "rgba(15, 23, 42, 0.06)"};
`;

const OptionLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ $status }) =>
    $status === "correct" ? "#10b981" :
    $status === "wrong"   ? "#dc2626" :
                            "rgba(15, 23, 42, 0.08)"};
  color: ${({ $status }) => ($status === "neutral" ? "#4b5563" : "#fff")};
  font-weight: 800;
  font-size: 12px;
`;

const OptionBody = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #1f2937;

  [data-theme="dark"] & { color: #e5e7eb; }
`;

const Tag = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $status }) =>
    $status === "correct" ? "#047857" :
    $status === "wrong"   ? "#b91c1c" : "#4b5563"};
`;
